(() => {
  'use strict';

  const ANNOUNCEMENT_SECTION = '.shopify-section-announcement-bar';
  const HEADER_SECTION = '.shopify-section-header';
  const HEADER_COMPONENT = 'header-component';
  const THRESHOLD = 36;
  const GROUP_CLASS = 'sticky-header-group';
  const HIDDEN_CLASS = 'is-sticky-group-hidden';
  const SHIFT_VAR = '--sticky-group-shift';

  let lastScrollY = window.scrollY;
  let isHidden = false;
  let ticking = false;
  let groupActive = false;
  let headerLocked = false;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isMobileViewport = () => window.matchMedia('(max-width: 767.98px)').matches;

  const stickyActiveForDevices = (devices) => {
    const mobile = isMobileViewport();
    if (!devices || devices === 'none') return false;
    if (devices === 'desktop_and_mobile') return true;
    if (devices === 'desktop_only') return !mobile;
    if (devices === 'mobile_only') return mobile;
    return false;
  };

  const getAnnouncementSection = () => document.querySelector(ANNOUNCEMENT_SECTION);

  const getHeaderSection = () => document.querySelector(HEADER_SECTION);

  const getHeaderComponent = () => document.querySelector(HEADER_COMPONENT);

  const isAnnouncementSticky = (announcementSection) => {
    if (!announcementSection) return false;
    const bar = announcementSection.querySelector('announcement-bar');
    if (!bar) return false;
    const devices = bar.getAttribute('data-sticky-devices') || 'desktop_and_mobile';
    return stickyActiveForDevices(devices);
  };

  const getHeaderStickyMode = (headerComponent) => {
    if (!headerComponent) return 'none';
    const attr = isMobileViewport() ? 'is-sticky-mobile' : 'is-sticky';
    return headerComponent.getAttribute(attr) || 'none';
  };

  const isHeaderSticky = (headerComponent) => {
    const mode = getHeaderStickyMode(headerComponent);
    return mode === 'static' || mode === 'dynamic';
  };

  const isHeaderDynamic = (headerComponent) => getHeaderStickyMode(headerComponent) === 'dynamic';

  const isAnnouncementDynamic = (announcementSection) => {
    const bar = announcementSection?.querySelector('announcement-bar[data-sticky-mode="dynamic"]');
    return Boolean(bar);
  };

  const shouldUseGroup = (announcementSection, headerComponent) => {
    if (prefersReducedMotion()) return false;
    if (!isAnnouncementSticky(announcementSection) || !isHeaderSticky(headerComponent)) return false;
    return isHeaderDynamic(headerComponent) || isAnnouncementDynamic(announcementSection);
  };

  const measureGroupHeight = (announcementSection, headerSection) => {
    const a = announcementSection?.offsetHeight || 0;
    const h = headerSection?.offsetHeight || 0;
    return a + h;
  };

  const lockHeaderDynamic = (headerComponent, locked) => {
    if (!headerComponent || typeof headerComponent.toggleLockDynamicShow !== 'function') return;
    headerComponent.toggleLockDynamicShow(locked);
    headerLocked = locked;
  };

  const setGroupHidden = (announcementSection, headerSection, hidden) => {
    isHidden = hidden;
    const height = measureGroupHeight(announcementSection, headerSection);
    const shift = hidden ? `-${height}px` : '0px';

    document.documentElement.style.setProperty(SHIFT_VAR, shift);

    [announcementSection, headerSection].forEach((section) => {
      if (!section) return;
      section.classList.toggle(HIDDEN_CLASS, hidden);
      section.classList.toggle('scrolled', window.scrollY > 8);
      section.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
  };

  const activateGroup = (announcementSection, headerSection, headerComponent) => {
    document.body.classList.add(GROUP_CLASS);
    announcementSection?.setAttribute('data-sticky-group', 'true');
    headerSection?.setAttribute('data-sticky-group', 'true');
    lockHeaderDynamic(headerComponent, true);
    setGroupHidden(announcementSection, headerSection, false);
    groupActive = true;
  };

  const deactivateGroup = (announcementSection, headerSection, headerComponent) => {
    document.body.classList.remove(GROUP_CLASS);
    document.documentElement.style.removeProperty(SHIFT_VAR);

    [announcementSection, headerSection].forEach((section) => {
      if (!section) return;
      section.classList.remove(HIDDEN_CLASS);
      section.removeAttribute('data-sticky-group');
      if (section.matches(ANNOUNCEMENT_SECTION)) {
        section.removeAttribute('aria-hidden');
        section.classList.remove('scrolled');
      }
    });

    lockHeaderDynamic(headerComponent, false);
    isHidden = false;
    groupActive = false;
  };

  const syncMode = () => {
    const announcementSection = getAnnouncementSection();
    const headerSection = getHeaderSection();
    const headerComponent = getHeaderComponent();
    const useGroup = shouldUseGroup(announcementSection, headerComponent);

    if (useGroup && !groupActive) {
      activateGroup(announcementSection, headerSection, headerComponent);
    } else if (!useGroup && groupActive) {
      deactivateGroup(announcementSection, headerSection, headerComponent);
    } else if (useGroup && groupActive) {
      // Recalcula altura se o grupo já estiver escondido (resize / fonts)
      if (isHidden) {
        setGroupHidden(announcementSection, headerSection, true);
      }
    }
  };

  const onScroll = () => {
    if (!groupActive) {
      lastScrollY = window.scrollY;
      return;
    }

    const announcementSection = getAnnouncementSection();
    const headerSection = getHeaderSection();
    const scrollY = window.scrollY;
    const distance = scrollY - lastScrollY;
    const groupHeight = measureGroupHeight(announcementSection, headerSection);
    const pastGroup = scrollY > groupHeight;

    if (scrollY <= 8) {
      setGroupHidden(announcementSection, headerSection, false);
      lastScrollY = scrollY;
      return;
    }

    announcementSection?.classList.add('scrolled');
    headerSection?.classList.add('scrolled');

    if (distance < 0 && isHidden && distance + THRESHOLD < 0 && pastGroup) {
      setGroupHidden(announcementSection, headerSection, false);
    } else if (distance > THRESHOLD && pastGroup) {
      setGroupHidden(announcementSection, headerSection, true);
    }

    lastScrollY = scrollY;
  };

  const onScrollRaf = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      onScroll();
      ticking = false;
    });
  };

  /**
   * Letreiro solo (barra dinâmica sem header sticky) — fallback leve.
   */
  const initSoloAnnouncement = () => {
    const bar = document.querySelector('announcement-bar[data-sticky-mode="dynamic"]');
    const section = bar?.closest(ANNOUNCEMENT_SECTION);
    if (!bar || !section) return;

    let soloHidden = false;
    let soloLastY = window.scrollY;

    const devices = bar.getAttribute('data-sticky-devices') || 'desktop_and_mobile';

    const syncSolo = () => {
      if (groupActive || !stickyActiveForDevices(devices) || prefersReducedMotion()) {
        section.removeAttribute('dynamic-sticky');
        section.removeAttribute('aria-hidden');
        section.classList.remove('scrolled');
        return false;
      }
      section.setAttribute('dynamic-sticky', '');
      return true;
    };

    const onSoloScroll = () => {
      if (groupActive || !syncSolo()) {
        soloLastY = window.scrollY;
        return;
      }

      const scrollY = window.scrollY;
      const distance = scrollY - soloLastY;

      if (scrollY <= 8) {
        section.classList.remove('scrolled');
        soloHidden = false;
        section.setAttribute('aria-hidden', 'false');
        soloLastY = scrollY;
        return;
      }

      section.classList.add('scrolled');

      if (distance < 0 && soloHidden && distance + THRESHOLD < 0 && scrollY > section.offsetHeight) {
        soloHidden = false;
        section.setAttribute('aria-hidden', 'false');
      } else if (distance > THRESHOLD && scrollY > section.offsetHeight) {
        soloHidden = true;
        section.setAttribute('aria-hidden', 'true');
      }

      soloLastY = scrollY;
    };

    syncSolo();
    window.addEventListener(
      'scroll',
      () => {
        requestAnimationFrame(onSoloScroll);
      },
      { passive: true }
    );
  };

  const boot = () => {
    syncMode();
    initSoloAnnouncement();
    window.addEventListener('scroll', onScrollRaf, { passive: true });
    window.addEventListener('resize', syncMode, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    syncMode();
  });

  // Expõe estado mínimo para debug / testes
  window.__stickyHeaderGroup = {
    isActive: () => groupActive,
    isHidden: () => isHidden,
    headerLocked: () => headerLocked,
  };
})();
